import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Clock, XCircle } from 'lucide-react';

// Get days in a month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Get the first day of the month (0 = Sunday, 6 = Saturday)
const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month, 1).getDay();
};

// Format date as YYYY-MM-DD
const formatDateStr = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Get day status from records
const getDayStatus = (records, dateStr) => {
  const dayRecords = records.filter(r => r.effectiveDate === dateStr);

  if (dayRecords.length === 0) {
    return { status: 'none', counts: { total: 0, processed: 0, issues: 0 } };
  }

  let processedCount = 0;
  let issueCount = 0;

  dayRecords.forEach(record => {
    if (record.status === 'needs_review') {
      issueCount++;
    } else if (record.status === 'processed') {
      processedCount++;
    } else if (record.status === 'missed' || record.status === 'failed') {
      issueCount++;
    }
  });

  if (issueCount > 0) {
    return {
      status: 'issues',
      counts: { total: dayRecords.length, processed: processedCount, issues: issueCount }
    };
  }

  if (processedCount === dayRecords.length && processedCount > 0) {
    return {
      status: 'all_processed',
      counts: { total: dayRecords.length, processed: processedCount, issues: 0 }
    };
  }

  return {
    status: 'partial',
    counts: { total: dayRecords.length, processed: processedCount, issues: issueCount }
  };
};

// CalendarDay Component
const CalendarDay = ({ date, dateStr, isCurrentMonth, isToday, isSelected, status, issueCount, onClick }) => {
  return (
    <button
      onClick={() => onClick(dateStr)}
      disabled={!isCurrentMonth}
      className={`
        relative flex flex-col items-center justify-center
        h-12 rounded-lg transition-all duration-150
        ${!isCurrentMonth ? 'text-gray-300 cursor-default' : 'hover:bg-gray-100 cursor-pointer'}
        ${isToday ? 'ring-2 ring-orange-400 ring-offset-1' : ''}
        ${isSelected ? 'bg-orange-50' : ''}
      `}
    >
      {/* Day number */}
      <span className={`
        text-sm font-medium
        ${isToday ? 'text-orange-600' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}
      `}>
        {date}
      </span>

      {/* Status indicator */}
      {isCurrentMonth && status !== 'none' && (
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
          {status === 'all_processed' && (
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          )}
          {status === 'issues' && (
            <>
              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {issueCount > 1 && (
                <span className="text-[9px] font-bold text-red-600">{issueCount}</span>
              )}
            </>
          )}
          {status === 'partial' && (
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
          )}
        </div>
      )}
    </button>
  );
};

// Stats Bar Component
const StatsBar = ({ stats }) => {
  return (
    <div className="flex items-center gap-4 py-2 px-4 bg-gray-50 rounded-lg text-xs">
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-gray-600">{stats.processed} Processed</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-red-500" />
        <span className="text-gray-600">{stats.missed} Missed</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-gray-600">{stats.needsReview} Review</span>
      </div>
    </div>
  );
};

const FileTrackingCalendar = ({ records = [], selectedDate, onSelectDate, loading = false }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  // Get days from previous month to fill the first week
  const prevMonthDays = getDaysInMonth(currentYear, currentMonth - 1);
  const prevMonthStart = prevMonthDays - firstDay + 1;

  // Navigate months
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    onSelectDate(formatDateStr(today.getFullYear(), today.getMonth(), today.getDate()));
  };

  // Calculate summary stats for the current month view
  const monthStats = useMemo(() => {
    const startDate = formatDateStr(currentYear, currentMonth, 1);
    const endDate = formatDateStr(currentYear, currentMonth, daysInMonth);

    const monthRecords = records.filter(r =>
      r.effectiveDate >= startDate && r.effectiveDate <= endDate
    );

    return {
      total: monthRecords.length,
      missed: monthRecords.filter(r => r.status === 'missed' || r.status === 'failed').length,
      processed: monthRecords.filter(r => r.status === 'processed').length,
      needsReview: monthRecords.filter(r => r.status === 'needs_review').length
    };
  }, [records, currentYear, currentMonth, daysInMonth]);

  const monthName = new Date(currentYear, currentMonth).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
  const todayStr = formatDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  // Build calendar grid
  const calendarDays = [];

  // Previous month days
  for (let i = 0; i < firstDay; i++) {
    const day = prevMonthStart + i;
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    calendarDays.push({
      date: day,
      dateStr: formatDateStr(prevYear, prevMonth, day),
      isCurrentMonth: false
    });
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = formatDateStr(currentYear, currentMonth, day);
    const { status, counts } = getDayStatus(records, dateStr);
    calendarDays.push({
      date: day,
      dateStr,
      isCurrentMonth: true,
      isToday: dateStr === todayStr,
      status,
      issueCount: counts.issues
    });
  }

  // Next month days to complete the grid (6 rows)
  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    calendarDays.push({
      date: day,
      dateStr: formatDateStr(nextYear, nextMonth, day),
      isCurrentMonth: false
    });
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevMonth}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <h3 className="text-base font-bold text-gray-900 min-w-[140px] text-center">
            {monthName}
          </h3>
          <button
            onClick={goToNextMonth}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        </div>
        <button
          onClick={goToToday}
          className="px-3 py-1.5 text-xs font-semibold text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
        >
          Today
        </button>
      </div>

      {/* Stats bar */}
      <StatsBar stats={monthStats} />

      {/* Day names header */}
      <div className="grid grid-cols-7 mt-3 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-[11px] font-medium text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => (
          <CalendarDay
            key={index}
            date={day.date}
            dateStr={day.dateStr}
            isCurrentMonth={day.isCurrentMonth}
            isToday={day.isToday}
            isSelected={selectedDate === day.dateStr}
            status={day.status || 'none'}
            issueCount={day.issueCount || 0}
            onClick={onSelectDate}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span>Processed</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
          <span>Partial</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
          <span>Issues</span>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-2xl">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      )}
    </div>
  );
};

export default FileTrackingCalendar;
